import React, { useEffect, useState } from 'react';
import styles from './GroupComplianceRow.module.scss';
import classNames from 'classnames';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getFullName, calculateUserStatistics } from '../../utils';
import Menu from '../atoms/Menu';
import VelocityPie from '../Clients/VelocityPie';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { UserType } from '../../constants';
import { locales } from '../../constants/locale';
import type {
  Member,
  SessionStatistic,
  UserStatistics,
} from '../../types';

const texts = locales.en.components.clients.userWorkoutsRow;

interface GroupComplianceRowProps {
  member: Member;
  trainerName: string | undefined;
  openEditClientModal: () => void;
  onRemoveMember: () => void;
  onGoToInDepthUserPage: () => void;
  userType: UserType;
  lastWorkoutSessionStatistic: SessionStatistic | null;
  last30DaysSessionStatistics: SessionStatistic[];
  allTimeSessionStatistics: SessionStatistic[];
}

const GroupComplianceRow: React.FC<GroupComplianceRowProps> = ({
  userType,
  member,
  trainerName,
  openEditClientModal,
  onRemoveMember,
  onGoToInDepthUserPage,
  lastWorkoutSessionStatistic,
  last30DaysSessionStatistics,
  allTimeSessionStatistics,
}) => {
  const [isRemoveMemberConfirmationOpen, setIsRemoveMemberConfirmationOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [userStatistics, setUserStatistics] = useState<UserStatistics | null>(
    null,
  );
  useEffect(() => {
    const statistics = calculateUserStatistics(
      lastWorkoutSessionStatistic,
      [], // last 7 days metrics not needed for compliance rows
      last30DaysSessionStatistics,
      allTimeSessionStatistics,
    );
    setUserStatistics(statistics);
  }, [
    lastWorkoutSessionStatistic,
    last30DaysSessionStatistics,
    allTimeSessionStatistics,
  ]);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const handleEditClient = () => {
    openEditClientModal();
    handleCloseMenuOptions();
  };

  const handleRemoveMember = async () => {
    handleCloseMenuOptions();
    await onRemoveMember();
    setIsRemoveMemberConfirmationOpen(false);
  };

  const handleGoToInDepthUserPage = () => {
    onGoToInDepthUserPage();
    handleCloseMenuOptions();
  };

  const renderUserName = () => (
    <div
      className={classNames(styles.userColumn, styles.userName)}
      onClick={onGoToInDepthUserPage}
    >
      <AccountCircleOutlinedIcon className={styles.userIcon} color="primary" />
      {getFullName(member)}
    </div>
  );

  const renderRemoveMemberConfirmationDialog = () => (
    <ConfirmationDialog
      text={`Are you sure you want to remove this ${userType.toLowerCase()} from the group?`}
      onSubmit={handleRemoveMember}
      submitText="Yes"
      cancelText="No"
      isOpen={isRemoveMemberConfirmationOpen}
      onCancel={() => setIsRemoveMemberConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const getUserOptions = () => {
    return [
      {
        onClick: handleEditClient,
        label: `Edit Member Details`,
        icon: <BorderColorOutlinedIcon color="primary" />,
      },
      {
        onClick: handleGoToInDepthUserPage,
        label: `Go to In-Depth User Page`,
        icon: <TrendingUpIcon color="primary" />,
      },
      {
        onClick: () => setIsRemoveMemberConfirmationOpen(true),
        label: `Remove Member`,
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      },
    ];
  };

  const renderMoreOptions = () => (
    <div className={classNames(styles.userColumn, styles.options)}>
      <PendingOutlinedIcon
        // @ts-ignore
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget as unknown as HTMLElement);
        }}
        className={styles.optionsBtn}
        color="primary"
      />
      <Menu
        anchorEl={anchorEl}
        isOpen={open}
        onClose={handleCloseMenuOptions}
        options={getUserOptions()}
      />
    </div>
  );

  const renderLastWorkoutVelocityPie = () => {
    const {
      lastWorkoutConcentricGoodRepsPercentage,
      lastWorkoutConcentricFastRepsPercentage,
      lastWorkoutConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !lastWorkoutConcentricGoodRepsPercentage &&
      !lastWorkoutConcentricFastRepsPercentage &&
      !lastWorkoutConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={lastWorkoutConcentricGoodRepsPercentage}
          fast={lastWorkoutConcentricFastRepsPercentage}
          slow={lastWorkoutConcentricSlowRepsPercentage}
          missed={userStatistics?.lastWorkoutConcentricMissedRepsPercentage}
          subtitle={texts.lastWorkout}
        />
      </div>
    );
  };

  const renderAllTimeVelocityPie = () => {
    const {
      allTimeConcentricGoodRepsPercentage,
      allTimeConcentricFastRepsPercentage,
      allTimeConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !allTimeConcentricGoodRepsPercentage &&
      !allTimeConcentricFastRepsPercentage &&
      !allTimeConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={allTimeConcentricGoodRepsPercentage}
          fast={allTimeConcentricFastRepsPercentage}
          slow={allTimeConcentricSlowRepsPercentage}
          missed={userStatistics?.allTimeConcentricMissedRepsPercentage}
          subtitle="All Time"
        />
      </div>
    );
  };

  const renderLastMonthVelocityPie = () => {
    const {
      lastMonthConcentricGoodRepsPercentage,
      lastMonthConcentricFastRepsPercentage,
      lastMonthConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !lastMonthConcentricGoodRepsPercentage &&
      !lastMonthConcentricFastRepsPercentage &&
      !lastMonthConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={lastMonthConcentricGoodRepsPercentage}
          fast={lastMonthConcentricFastRepsPercentage}
          slow={lastMonthConcentricSlowRepsPercentage}
          missed={userStatistics?.lastMonthConcentricMissedRepsPercentage}
          subtitle={texts.last30Days}
        />
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.userWorkoutsRow, styles.workoutsListGrid)}
    >
      {renderUserName()}
      {renderLastWorkoutVelocityPie()}
      {renderLastMonthVelocityPie()}
      {renderAllTimeVelocityPie()}
      {renderMoreOptions()}
      {renderRemoveMemberConfirmationDialog()}
    </div>
  );
};

export default GroupComplianceRow;
